import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import './AdminPriceManager.css';

function AdminPriceManager() {
  const { fetchWithAuth } = useAuth();

  // Data
  const [prices, setPrices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filters, setFilters] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState('all');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterRetailer, setFilterRetailer] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Sorting
  const [sortBy, setSortBy] = useState('scrapedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Success message
  const [successMsg, setSuccessMsg] = useState('');

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const res = await fetchWithAuth('/api/admin/prices/filters');
        if (res.ok) {
          const data = await res.json();
          setFilters(data);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Filter:', err);
      }
    };
    loadFilters();
  }, [fetchWithAuth]);

  // Load prices
  const loadPrices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        sortBy,
        order: sortOrder,
        limit: pageSize,
        offset: page * pageSize
      });

      if (searchQuery) params.set('query', searchQuery);
      if (filterManufacturer !== 'all') params.set('manufacturer', filterManufacturer);
      if (filterProduct !== 'all') params.set('product', filterProduct);
      if (filterRetailer !== 'all') params.set('retailer', filterRetailer);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);

      const res = await fetchWithAuth(`/api/admin/prices?${params.toString()}`);
      if (!res.ok) throw new Error('Fehler beim Laden der Preise');

      const data = await res.json();
      setPrices(data.items);
      setTotal(data.total);
      setSelectedIds(new Set());
      setSelectAll(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, sortBy, sortOrder, page, pageSize, searchQuery, filterManufacturer, filterProduct, filterRetailer, dateFrom, dateTo]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  // Sorting handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Editing
  const startEdit = (price) => {
    setEditingId(price._id);
    setEditValues({
      price: price.price,
      retailer: price.retailer,
      inStock: price.inStock,
      isOutlier: price.isOutlier || false,
      outlierReason: price.outlierReason || '',
      url: price.url || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/admin/prices/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price: Number(editValues.price),
          retailer: editValues.retailer,
          inStock: editValues.inStock,
          isOutlier: editValues.isOutlier,
          outlierReason: editValues.outlierReason,
          url: editValues.url
        })
      });

      if (!res.ok) throw new Error('Fehler beim Speichern');

      showSuccess('Preis aktualisiert ✓');
      setEditingId(null);
      loadPrices();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete single
  const deletePrice = async (id) => {
    if (!window.confirm('Diesen Preis wirklich löschen?')) return;

    try {
      const res = await fetchWithAuth(`/api/admin/prices/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Fehler beim Löschen');

      showSuccess('Preis gelöscht ✓');
      loadPrices();
    } catch (err) {
      setError(err.message);
    }
  };

  // Bulk delete
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} Preise wirklich löschen?`)) return;

    try {
      const res = await fetchWithAuth('/api/admin/prices/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });

      if (!res.ok) throw new Error('Fehler beim Löschen');

      const data = await res.json();
      showSuccess(`${data.deletedCount} Preise gelöscht ✓`);
      loadPrices();
    } catch (err) {
      setError(err.message);
    }
  };

  // Selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(prices.map(p => p._id)));
    }
    setSelectAll(!selectAll);
  };

  // Success message
  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  // Format helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return '–';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '–';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (price == null) return '–';
    return `${price.toFixed(2)} €`;
  };

  const totalPages = Math.ceil(total / pageSize);

  // Reset filters
  const resetFilters = () => {
    setSearchQuery('');
    setFilterManufacturer('all');
    setFilterProduct('all');
    setFilterRetailer('all');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  return (
    <div className="admin-price-manager">
      {/* Header */}
      <div className="apm-header">
        <h2>💰 Preise verwalten</h2>
        <span className="apm-count">{total.toLocaleString('de-DE')} Preise</span>
      </div>

      {successMsg && <div className="apm-success">{successMsg}</div>}
      {error && <div className="apm-error">{error}</div>}

      {/* Filter Bar */}
      <div className="apm-filters">
        <div className="apm-filters-row">
          <div className="apm-search-box">
            <span className="apm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Produkt oder Shop suchen..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              className="apm-search-input"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setPage(0); }} className="apm-search-clear">✕</button>
            )}
          </div>

          <select
            value={filterManufacturer}
            onChange={(e) => { setFilterManufacturer(e.target.value); setPage(0); }}
            className="apm-filter-select"
          >
            <option value="all">Alle Hersteller</option>
            {filters?.manufacturers?.map(m => (
              <option key={m._id} value={m._id}>{m.name}</option>
            ))}
          </select>

          <select
            value={filterProduct}
            onChange={(e) => { setFilterProduct(e.target.value); setPage(0); }}
            className="apm-filter-select"
          >
            <option value="all">Alle Produkte</option>
            {filters?.products?.map(p => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>

          <select
            value={filterRetailer}
            onChange={(e) => { setFilterRetailer(e.target.value); setPage(0); }}
            className="apm-filter-select"
          >
            <option value="all">Alle Shops</option>
            {filters?.retailers?.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="apm-filters-row">
          <div className="apm-date-range">
            <label>Von:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              className="apm-date-input"
            />
            <label>Bis:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              className="apm-date-input"
            />
          </div>

          <button onClick={resetFilters} className="apm-reset-btn">
            🔄 Filter zurücksetzen
          </button>

          {selectedIds.size > 0 && (
            <button onClick={bulkDelete} className="apm-bulk-delete-btn">
              🗑️ {selectedIds.size} löschen
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="apm-table-wrapper">
        {loading ? (
          <div className="apm-loading">⏳ Preise werden geladen...</div>
        ) : prices.length === 0 ? (
          <div className="apm-empty">Keine Preise gefunden</div>
        ) : (
          <table className="apm-table">
            <thead>
              <tr>
                <th className="apm-col-check">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="apm-col-date sortable" onClick={() => handleSort('scrapedAt')}>
                  Datum {getSortIcon('scrapedAt')}
                </th>
                <th className="apm-col-time">Uhrzeit</th>
                <th className="apm-col-manufacturer sortable" onClick={() => handleSort('manufacturerName')}>
                  Hersteller {getSortIcon('manufacturerName')}
                </th>
                <th className="apm-col-product sortable" onClick={() => handleSort('productName')}>
                  Produkt {getSortIcon('productName')}
                </th>
                <th className="apm-col-retailer sortable" onClick={() => handleSort('retailer')}>
                  Shop {getSortIcon('retailer')}
                </th>
                <th className="apm-col-price sortable" onClick={() => handleSort('price')}>
                  Preis {getSortIcon('price')}
                </th>
                <th className="apm-col-stock">Status</th>
                <th className="apm-col-actions">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {prices.map(p => (
                <tr
                  key={p._id}
                  className={`${selectedIds.has(p._id) ? 'selected' : ''} ${p.isOutlier ? 'outlier' : ''} ${editingId === p._id ? 'editing' : ''}`}
                >
                  <td className="apm-col-check">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p._id)}
                      onChange={() => toggleSelect(p._id)}
                    />
                  </td>
                  <td className="apm-col-date">{formatDate(p.scrapedAt)}</td>
                  <td className="apm-col-time">{formatTime(p.scrapedAt)}</td>
                  <td className="apm-col-manufacturer">
                    <span className="apm-manufacturer-badge">{p.manufacturerName}</span>
                  </td>
                  <td className="apm-col-product">
                    <span className="apm-product-name" title={p.productName}>
                      {p.productName || '(Unbekannt)'}
                    </span>
                  </td>
                  <td className="apm-col-retailer">
                    {editingId === p._id ? (
                      <input
                        type="text"
                        value={editValues.retailer}
                        onChange={(e) => setEditValues(prev => ({ ...prev, retailer: e.target.value }))}
                        className="apm-edit-input"
                      />
                    ) : (
                      <span>{p.retailer}</span>
                    )}
                  </td>
                  <td className="apm-col-price">
                    {editingId === p._id ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editValues.price}
                        onChange={(e) => setEditValues(prev => ({ ...prev, price: e.target.value }))}
                        className="apm-edit-input apm-edit-price"
                      />
                    ) : (
                      <span className={`apm-price-value ${p.isOutlier ? 'outlier-price' : ''}`}>
                        {formatPrice(p.price)}
                      </span>
                    )}
                  </td>
                  <td className="apm-col-stock">
                    {editingId === p._id ? (
                      <select
                        value={editValues.inStock ? 'true' : 'false'}
                        onChange={(e) => setEditValues(prev => ({ ...prev, inStock: e.target.value === 'true' }))}
                        className="apm-edit-select"
                      >
                        <option value="true">✅ Lager</option>
                        <option value="false">❌ Aus</option>
                      </select>
                    ) : (
                      <span className={`apm-stock-badge ${p.inStock ? 'in-stock' : 'out-of-stock'}`}>
                        {p.inStock ? '✅' : '❌'}
                      </span>
                    )}
                  </td>
                  <td className="apm-col-actions">
                    {editingId === p._id ? (
                      <div className="apm-action-btns">
                        <button onClick={() => saveEdit(p._id)} className="apm-save-btn" title="Speichern">💾</button>
                        <button onClick={cancelEdit} className="apm-cancel-btn" title="Abbrechen">✕</button>
                      </div>
                    ) : (
                      <div className="apm-action-btns">
                        <button onClick={() => startEdit(p)} className="apm-edit-btn" title="Bearbeiten">✏️</button>
                        {p.url && (
                          <a href={p.url} target="_blank" rel="noopener noreferrer" className="apm-link-btn" title="URL öffnen">🔗</a>
                        )}
                        <button onClick={() => deletePrice(p._id)} className="apm-delete-btn" title="Löschen">🗑️</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="apm-pagination">
          <button
            disabled={page === 0}
            onClick={() => setPage(0)}
            className="apm-page-btn"
          >
            ⏮
          </button>
          <button
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
            className="apm-page-btn"
          >
            ◀
          </button>
          <span className="apm-page-info">
            Seite {page + 1} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
            className="apm-page-btn"
          >
            ▶
          </button>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage(totalPages - 1)}
            className="apm-page-btn"
          >
            ⏭
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminPriceManager;
